import { useState } from 'react';
import { Trans } from 'react-i18next';

import { actions, LoadResults } from 'entities';

import { Alert, Card, LabeledTextarea, Button, Column, Row } from 'components/Blocks';
import { ApproveRejectButtons } from 'components/Buttons';
import { PropertyWithIcon } from 'components/PropertyWithIcon';
import { UserAvatar } from 'components/UserAvatar';
import { EntityLoader } from 'components/Entities';
import { SendIcon, UserIcon } from 'components/Icons';

import { ProjectValueView } from './ProjectValue';
import { ProjectLink } from '../components';

import { useToggleV2 } from 'hooks';
import { useLang } from 'hooks/lang';

interface RequestProps {
  request: LoadResults['invoice.invoiceControlCancelRequests'][number];

  removeSelf(): void;
}

export function SingleInvoiceControlCancelRequest({ request, removeSelf }: RequestProps) {
  const { t } = useLang();

  const { id, project, requestedBy, justification } = request;

  const [approved, approvedHandlers] = useToggleV2();
  const [comment, setComment] = useState('');

  const needsInput = approved ? false : !comment.trim();

  const answerRequest = actions.invoice.answerInvoiceControlCancelation.use({
    block: needsInput,

    params: {
      projectId: project.id,
      requestId: id,
      approved,
      comment,
    },

    onSuccess: removeSelf,
  });

  return (
    <Card flow="column" className="p-5" gap="6">
      <Row justifyContent="space-between" className="small_phone:flex-col">
        <ProjectLink
          id={project.id}
          text={<span className="text-lg font-semibold text-primary">{project.name}</span>}
        />

        <EntityLoader target="users.find" params={{ id: project.salesPerson }}>
          {({ data }) => (
            <UserAvatar
              user={data}
              name={t('sales-person')!}
              nameClassName="font-medium"
              nameSide="left"
              showNameOnHover
            />
          )}
        </EntityLoader>
      </Row>

      <Column gap="6">
        <PropertyWithIcon icon={<UserIcon />} name={t('client')} value={project.clientName} />

        <ProjectValueView project={project} />
      </Column>

      <Column gap="2">
        <Row gap="2">
          <EntityLoader target="users.find" params={{ id: requestedBy }}>
            {({ data }) => <UserAvatar user={data} />}
          </EntityLoader>

          <p className="font-medium">{t('user-comment')}</p>
        </Row>

        <p className="rounded-lg border bg-muted p-3">{justification}</p>
      </Column>

      <Column gap="6">
        <LabeledTextarea
          label={t('comments') as string}
          value={comment}
          onChange={({ target: { value } }) => setComment(value)}
        />

        <Alert level="warning">
          <Trans i18nKey="project-invoicing-sales-cancel-warning" />
        </Alert>

        <Row justifyContent="space-between" className="small_phone:flex-col">
          <ApproveRejectButtons
            value={approved}
            onChange={approved ? approvedHandlers.turnOff : approvedHandlers.turnOn}
          />

          <Button disabled={needsInput} {...answerRequest.buttonProps}>
            <SendIcon />
            {t('answer-request')}
          </Button>
        </Row>
      </Column>
    </Card>
  );
}
